/**
 * Pluggable bearer-token storage abstraction.
 *
 * The API client never reaches into `localStorage`, `sessionStorage`, or any
 * Capacitor-specific API directly. Consumers inject a `TokenStore` so the
 * same client works in:
 *   - Node / SSR / unit tests        → `createMemoryTokenStore()`
 *   - Browser (Vite SPA)             → `createLocalStorageTokenStore()`
 *   - iOS / Android (Capacitor)      → `createCapacitorTokenStore()`
 *
 * Every method returns a Promise — Capacitor's `Preferences` API is async,
 * and async-by-default keeps the consumer code identical across platforms.
 */
export interface TokenStore {
  /** Returns the stored bearer token, or `null` if none. */
  get(): Promise<string | null>;
  /** Persists a bearer token (overwrites any previous value). */
  set(token: string): Promise<void>;
  /** Removes any stored bearer token. */
  clear(): Promise<void>;
}

// ---------- In-memory (default, test-safe) -------------------------------

/**
 * Volatile, in-process token store. Wiped on page reload — useful as a default
 * for tests, SSR, or environments where you do NOT want persistence.
 */
export function createMemoryTokenStore(): TokenStore {
  let token: string | null = null;
  return {
    async get() {
      return token;
    },
    async set(next) {
      token = next;
    },
    async clear() {
      token = null;
    },
  };
}

// ---------- Browser: localStorage ----------------------------------------

/**
 * Persists the bearer token in `localStorage` under `key` (default
 * `draftedge_token`).
 *
 * Falls back to an in-memory store if `localStorage` throws (private mode,
 * embedded WebView with storage disabled, etc.) — so callers never need to
 * try/catch token operations.
 */
export function createLocalStorageTokenStore(
  key = 'draftedge_token',
): TokenStore {
  // SSR / non-DOM environments — fall back to memory.
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return createMemoryTokenStore();
  }

  // Probe storage once. Some embedded WebViews throw on access.
  try {
    const probeKey = `${key}__probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
  } catch {
    return createMemoryTokenStore();
  }

  return {
    async get() {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    async set(token) {
      try {
        window.localStorage.setItem(key, token);
      } catch {
        // Swallow — token simply won't persist this session.
      }
    },
    async clear() {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // No-op.
      }
    },
  };
}

// ---------- Capacitor: secure native preferences -------------------------

/**
 * Capacitor (iOS/Android) token store backed by `@capacitor/preferences`.
 *
 * SCAFFOLD ONLY — this currently delegates to localStorage so the web build
 * keeps working without pulling in a peer dep we don't ship yet.
 *
 * For Phase 4 (mobile launch), replace the body with the 5-line real impl:
 *
 *   import { Preferences } from '@capacitor/preferences';
 *   return {
 *     async get()        { return (await Preferences.get({ key })).value; },
 *     async set(token)   { await Preferences.set({ key, value: token }); },
 *     async clear()      { await Preferences.remove({ key }); },
 *   };
 *
 * (Add `@capacitor/preferences` as a dep of `@draftedge/mobile`, NOT of
 * `@draftedge/api-client` — keep this package framework-agnostic.)
 */
export function createCapacitorTokenStore(
  key = 'draftedge_token',
): TokenStore {
  // TODO(phase-4): swap to @capacitor/preferences (see comment above).
  return createLocalStorageTokenStore(key);
}
